import { api } from "@/lib/api";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

/**
 * Handles a Buy Now click:
 * - Records the affiliate click on the backend (idempotent per user+product).
 * - Fires analytics.
 * - Opens the merchant / affiliate URL in a new tab.
 * - Shows a "pending approval" toast — no rewards are credited until admin
 *   confirms the purchase.
 * Safe to call for logged-out users too — we just skip the backend record.
 */
export async function handleBuyClick(product, { user } = {}) {
  const productId = product?.id;
  const fallbackUrl =
    product?.merchant_product_url ||
    product?.affiliate_url ||
    product?.brand_url ||
    "";

  trackEvent("BUY_BUTTON_CLICK", { product_id: productId });

  // Open the tab SYNCHRONOUSLY inside the click handler so Safari/Firefox
  // popup blockers don't intercept it. We navigate the placeholder to the
  // real affiliate URL after the /purchase call resolves.
  const popup = fallbackUrl ? window.open(fallbackUrl, "_blank", "noopener,noreferrer") : null;

  if (!user) {
    trackEvent("AFFILIATE_LINK_CLICK", { product_id: productId, meta: { auth: "guest" } });
    toast("Sign in to earn rewards on your next purchase", {
      description: "Cashback and GreenCoins are credited only for signed-in shoppers.",
    });
    return { affiliate_url: fallbackUrl, guest: true };
  }

  let affiliateUrl = fallbackUrl;
  let duplicate = false;
  let message = "";
  try {
    const { data } = await api.post("/purchase", { product_id: productId });
    affiliateUrl = data.affiliate_url || fallbackUrl;
    duplicate = !!data.duplicate;
    message = data.message || "";
    // Update the pre-opened tab if the server returned a different URL.
    if (popup && affiliateUrl && affiliateUrl !== fallbackUrl) {
      try { popup.location.replace(affiliateUrl); } catch { /* cross-origin */ }
    }
  } catch (e) {
    console.warn("purchase click failed", e);
  }

  trackEvent("AFFILIATE_LINK_CLICK", { product_id: productId, meta: { duplicate } });

  if (!popup && affiliateUrl) {
    // Fallback if the synchronous open was blocked (or no fallback URL existed).
    window.open(affiliateUrl, "_blank", "noopener,noreferrer");
  } else if (!affiliateUrl) {
    toast.error("No merchant link is available for this product yet.");
  }

  if (duplicate) {
    toast("Rewards already pending for this item", {
      description:
        "You've clicked Buy before. Rewards will be credited once the merchant confirms your purchase.",
    });
  } else {
    toast.success("Redirecting to merchant", {
      description:
        message ||
        `+${product.greencoins || product.green_points || 0} coins & ₹${product.cashback} are pending. They'll be credited after your purchase is confirmed.`,
    });
  }

  return { affiliate_url: affiliateUrl, duplicate };
}
